import { parseIniInt } from './KnyttUtil';
import { KnyttPoint } from './KnyttPoint';
import type { KnyttWorld } from './KnyttWorld';

// JuniValues.cs VISITED_LIMIT: visited areas aren't tracked on huge worlds.
const VISITED_LIMIT = 160_000_000;

/** Case-insensitive string set (C# HashSet with OrdinalIgnoreCase) — stores lowercase. */
export class LowerCaseSet extends Set<string> {
  override add(value: string): this {
    return super.add(value.toLowerCase());
  }
  override has(value: string): boolean {
    return super.has(value.toLowerCase());
  }
  override delete(value: string): boolean {
    return super.delete(value.toLowerCase());
  }
}

export enum PowerNames {
  Run = 0,
  Climb = 1,
  DoubleJump = 2,
  HighJump = 3,
  Eye = 4,
  EnemyDetector = 5,
  Umbrella = 6,
  Hologram = 7,
  RedKey = 8,
  YellowKey = 9,
  BlueKey = 10,
  PurpleKey = 11,
  Map = 12,
}

export enum Collectable {
  None,
  User,
  Powerup,
  Creature,
  Coin,
  Artifact,
  Multiple,
  Key,
  RedKey,
  YellowKey,
  BlueKey,
  PurpleKey,
  Door,
  RedDoor,
  YellowDoor,
  BlueDoor,
  PurpleDoor,
}

export const COLLECT_CHAR = [
  '', 'm', 'p', '#', 'c', 'a', '+', 'k', 'r', 'y', 'b', 'u', 'd', 'R', 'Y', 'B', 'U',
];

export interface Flag {
  power: boolean;
  coin: boolean;
  trueFlag: boolean;
  number: number;
}

export function parseFlag(str: string | null | undefined): Flag | null {
  if (str == null) return null;
  const lower = str.toLowerCase();
  if (lower === 'true') return { power: false, coin: false, trueFlag: true, number: 0 };
  const power = lower.startsWith('power');
  const coin = lower.startsWith('coin');
  const num = parseIniInt(power ? str.substring(5) : coin ? str.substring(4) : str);
  if (num == null) return null;
  return { power, coin, trueFlag: false, number: num };
}

/**
 * The mutable run state for Juni: powers, story flags, and collectables.
 * Ported from YKnyttLib.JuniValues.
 */
export class JuniValues {
  powers: boolean[] = new Array(13).fill(false);
  flags: boolean[] = new Array(10).fill(false);
  collectables: boolean[] = new Array(200).fill(false);
  coinsSpent = 0;
  visited = new Set<number>(); // map indices
  marked = new Map<string, string>(); // KnyttPoint.key -> collected chars
  attachment: string | null = null;
  character: string | null = null;
  // Current area tint as raw strings (Tint/TintInk/TintTrans), persisted in the save.
  tint: [string | null, string | null, string | null] = [null, null, null];
  cutscenes: Set<string> = new LowerCaseSet();
  endings: Set<string> = new LowerCaseSet();
  totalDeaths = 0;
  hardestPlace: string | null = null;
  hardestPlaceDeaths = 0;
  totalTime = 0; // seconds of unpaused play
  private respawnArea: KnyttPoint | null = null;
  private respawnPosition: KnyttPoint | null = null;
  private currentPlaceDeaths = 0;

  /** JuniValues.cs setVisited(): skips huge worlds and out-of-bounds map positions. */
  setVisited(world: KnyttWorld, pos: KnyttPoint): void {
    if (world.getMapLength() > VISITED_LIMIT) return;
    if (
      pos.x < world.minBounds.x ||
      pos.x > world.maxBounds.x ||
      pos.y < world.minBounds.y ||
      pos.y > world.maxBounds.y
    ) {
      return;
    }
    this.visited.add(world.getMapIndex(pos));
  }

  /** Ported from JuniValues.cs respawn(): tracks total + per-savepoint death counts. */
  respawnDeath(area: KnyttPoint, position: KnyttPoint): void {
    this.totalDeaths++;
    if (!this.respawnArea?.equals(area) || !this.respawnPosition?.equals(position)) {
      this.currentPlaceDeaths = 0;
      this.respawnArea = area;
      this.respawnPosition = position;
    }
    this.currentPlaceDeaths++;
    if (this.currentPlaceDeaths > this.hardestPlaceDeaths) {
      this.hardestPlace = `x${area.x}y${area.y}:x${position.x}y${position.y}`;
      this.hardestPlaceDeaths = this.currentPlaceDeaths;
    }
  }

  /** Map-power reveal marks for uncollected items (ObjectLayers.checkCollectables). */
  setMark(pos: KnyttPoint, c: Collectable): void {
    const key = pos.key;
    const ch = COLLECT_CHAR[c];
    const cur = this.marked.get(key) ?? '';
    if (cur.includes(ch)) return;
    this.marked.set(key, cur + ch);
  }
  unsetMark(pos: KnyttPoint, c: Collectable): void {
    const key = pos.key;
    const cur = this.marked.get(key);
    if (cur == null) return;
    const next = cur.split(COLLECT_CHAR[c]).join('');
    if (next.length === 0) this.marked.delete(key);
    else this.marked.set(key, next);
  }
  /** Clears an area's marks (except a manual "User" mark) before rescanning it. */
  resetMarks(pos: KnyttPoint): void {
    const key = pos.key;
    const cur = this.marked.get(key);
    if (cur == null) return;
    if (cur.includes(COLLECT_CHAR[Collectable.User])) this.marked.set(key, COLLECT_CHAR[Collectable.User]);
    else this.marked.delete(key);
  }

  getPower(name: PowerNames): boolean {
    return this.powers[name];
  }
  setPower(name: PowerNames, val: boolean): void {
    this.powers[name] = val;
  }
  getFlag(i: number): boolean {
    return this.flags[i];
  }
  setFlag(i: number, val: boolean): void {
    this.flags[i] = val;
  }
  getCollectable(i: number): boolean {
    return this.collectables[i];
  }
  setCollectable(i: number, val: boolean): void {
    this.collectables[i] = val;
  }

  check(flag: Flag | null): boolean {
    if (!flag) return false;
    if (flag.trueFlag) return true;
    if (flag.power) return this.powers[flag.number];
    if (flag.coin) return this.collectables[flag.number + 50];
    return this.flags[flag.number];
  }

  getCreaturesCount(): number {
    let n = 0;
    for (let i = 1; i <= 50; i++) if (this.collectables[i]) n++;
    return n;
  }
  getCoinCount(): number {
    let n = 0;
    for (let i = 51; i <= 150; i++) if (this.collectables[i]) n++;
    return n - this.coinsSpent;
  }
  getArtifactsCount(): number {
    let n = 0;
    for (let i = 151; i <= 199; i++) if (this.collectables[i]) n++;
    return n;
  }
  /** Artifacts in group i (0-based, 7 per group). */
  getArtifactsCountGroup(i: number): number {
    let n = 0;
    for (let j = 151 + i * 7; j < 151 + (i + 1) * 7 && j <= 199; j++) {
      if (this.collectables[j]) n++;
    }
    return n;
  }

  /** Deep-ish clone for respawn (keeps powers/flags/collectables snapshot). */
  clone(): JuniValues {
    const v = new JuniValues();
    v.powers = [...this.powers];
    v.flags = [...this.flags];
    v.collectables = [...this.collectables];
    v.coinsSpent = this.coinsSpent;
    v.visited = new Set(this.visited);
    v.marked = new Map(this.marked);
    v.attachment = this.attachment;
    v.character = this.character;
    v.tint = [...this.tint];
    v.cutscenes = new LowerCaseSet(this.cutscenes);
    v.endings = new LowerCaseSet(this.endings);
    v.totalDeaths = this.totalDeaths;
    v.hardestPlace = this.hardestPlace;
    v.hardestPlaceDeaths = this.hardestPlaceDeaths;
    v.totalTime = this.totalTime;
    v.respawnArea = this.respawnArea;
    v.respawnPosition = this.respawnPosition;
    v.currentPlaceDeaths = this.currentPlaceDeaths;
    return v;
  }
}
